import type { ReactNode } from "react";
import { Search, LayoutGrid, List, Table2, ArrowUp, ArrowDown, RefreshCw, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CommunicationSortField } from "@/types/communications";
import type { SortDirection, ViewMode } from "@/hooks/use-view-preferences";

interface CommunicationInboxToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  sortField: CommunicationSortField;
  sortDirection: SortDirection;
  onSortChange: (field: CommunicationSortField, direction: SortDirection) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  disabled?: boolean;
  endSlot?: ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function CommunicationInboxToolbar({
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Search messages...",
  sortField,
  sortDirection,
  onSortChange,
  viewMode,
  onViewModeChange,
  disabled = false,
  endSlot,
  onRefresh,
  isRefreshing = false,
}: CommunicationInboxToolbarProps) {
  const sortValue = `${sortField}-${sortDirection}`;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative flex-1 min-w-[200px] max-w-md">
        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-8 h-9 text-sm"
          disabled={disabled}
        />
      </div>

      {endSlot}

      {onRefresh && (
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onRefresh}
          disabled={disabled || isRefreshing}
          title="Reload emails"
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      )}

      <Select
        value={sortValue}
        onValueChange={(value) => {
          const [field, direction] = value.split("-") as [CommunicationSortField, SortDirection];
          onSortChange(field, direction);
        }}
        disabled={disabled}
      >
        <SelectTrigger className="w-[140px] h-9 text-sm">
          <SelectValue placeholder="Sort by" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="date-desc">
            <div className="flex items-center gap-2">
              <ArrowDown className="h-3 w-3" />
              <span>Newest</span>
            </div>
          </SelectItem>
          <SelectItem value="date-asc">
            <div className="flex items-center gap-2">
              <ArrowUp className="h-3 w-3" />
              <span>Oldest</span>
            </div>
          </SelectItem>
          <SelectItem value="subject-asc">
            <div className="flex items-center gap-2">
              <ArrowUp className="h-3 w-3" />
              <span>Subject A → Z</span>
            </div>
          </SelectItem>
          <SelectItem value="subject-desc">
            <div className="flex items-center gap-2">
              <ArrowDown className="h-3 w-3" />
              <span>Subject Z → A</span>
            </div>
          </SelectItem>
          <SelectItem value="from-asc">
            <div className="flex items-center gap-2">
              <ArrowUp className="h-3 w-3" />
              <span>From A → Z</span>
            </div>
          </SelectItem>
          <SelectItem value="from-desc">
            <div className="flex items-center gap-2">
              <ArrowDown className="h-3 w-3" />
              <span>From Z → A</span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      <div className="flex items-center gap-0.5 border rounded-md p-0.5">
        <Button
          variant={viewMode === "grid" ? "secondary" : "ghost"}
          size="icon"
          className="h-7 w-7"
          onClick={() => onViewModeChange("grid")}
          title="Grid view"
          disabled={disabled}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={viewMode === "list" ? "secondary" : "ghost"}
          size="icon"
          className="h-7 w-7"
          onClick={() => onViewModeChange("list")}
          title="List view"
          disabled={disabled}
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={viewMode === "table" ? "secondary" : "ghost"}
          size="icon"
          className="h-7 w-7"
          onClick={() => onViewModeChange("table")}
          title="Table view"
          disabled={disabled}
        >
          <Table2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
